/**
 * <StatusBadge>
 *
 * Suppliers Table 行内的状态徽标。基于 Figma 节点 2716:71485 还原。
 *
 * 状态色映射：
 *  - quote-ready    → gray bg, dark text (Completed)
 *  - clarify-needed → amber/yellow bg (Need clarify)
 *  - awaiting-reply → blue bg (Waiting reply)
 *  - ai-thinking    → blue bg (AI thinking)
 */

import { FC } from 'react';
import cn from 'classnames';
import type { SupplierStatus } from '../../types';

export type StatusBadgeProps = {
  status: SupplierStatus;
  /** 已派生的显示文案 */
  label: string;
};

const STATUS_CLASS_MAP: Record<SupplierStatus, { badge: string; dot: string }> = {
  'quote-ready': {
    badge: 'bg-[#E5E5E5] text-[#262626]',
    dot: 'bg-[#737373]',
  },
  'clarify-needed': {
    badge: 'bg-[#FEF3C7] text-[#9A3412]',
    dot: 'bg-[#92400E]',
  },
  'awaiting-reply': {
    badge: 'bg-[#DBEAFE] text-[#1E40AF]',
    dot: 'bg-[#1E40AF]',
  },
  'ai-thinking': {
    badge: 'bg-[#DBEAFE] text-[#1E40AF]',
    dot: 'bg-[#1E40AF]',
  },
  completed: {
    badge: 'bg-[#E5E5E5] text-[#262626]',
    dot: 'bg-[#737373]',
  },
};

/** Figma 中显示的 label 映射 */
const STATUS_DISPLAY_MAP: Record<SupplierStatus, string> = {
  'quote-ready': 'Quote ready',
  'clarify-needed': 'Need clarify',
  'awaiting-reply': 'Waiting reply',
  'ai-thinking': 'AI thinking',
  completed: 'Completed',
};

export const StatusBadge: FC<StatusBadgeProps> = ({ status, label }) => {
  const classes = STATUS_CLASS_MAP[status];
  const displayLabel = label || STATUS_DISPLAY_MAP[status];

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 px-2 h-6 rounded-lg text-xs font-medium whitespace-nowrap',
        classes.badge
      )}
    >
      <span className={cn('w-1.5 h-1.5 rounded-full shrink-0', classes.dot)} />
      {displayLabel}
    </span>
  );
};

export default StatusBadge;
